import { useEffect, useState } from "react";
import { Loader2, LogOut, KeyRound, Coins, ScanFace } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PageHeader, Field } from "../components/common";
import { RupiahInput } from "../components/inputs";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";

const Card = ({ icon: Icon, title, desc, children }) => (
  <section className="fade-up rounded-2xl border border-gray-200 bg-white p-5 sm:p-4">
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-5 w-5" /></div>
      <div><h2 className="font-heading text-lg font-semibold text-gray-900">{title}</h2>{desc && <p className="text-sm text-gray-500">{desc}</p>}</div>
    </div>
    {children}
  </section>
);

const TarifForm = () => {
  const [tarif, setTarif] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then((r) => setTarif(String(r.data.tarif_per_voucher))); }, []);
  const save = async (e) => {
    e.preventDefault();
    if (!(Number(tarif) > 0)) return toast.error("Tarif harus lebih dari Rp0");
    setSaving(true);
    try { await api.put("/settings", { tarif_per_voucher: Number(tarif) }); toast.success("Tarif per voucher disimpan"); }
    catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex-1"><Field label="Tarif per voucher"><RupiahInput value={tarif} onChange={setTarif} testid="settings-tarif-input" /></Field></div>
      <Button data-testid="settings-tarif-save" type="submit" disabled={saving} className="h-10 rounded-full px-6">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Simpan tarif</Button>
    </form>
  );
};

const ThresholdForm = () => {
  const [s, setS] = useState(null);
  const [val, setVal] = useState("");
  useEffect(() => { api.get("/settings").then((r) => { setS(r.data); setVal(String(r.data.face_threshold)); }); }, []);
  const save = async (e) => {
    e.preventDefault();
    const n = Number(val.replace(",", "."));
    if (!(n > 0 && n < 1.5)) return toast.error("Ambang harus antara 0 dan 1,5");
    try { await api.put("/settings", { face_threshold: n }); toast.success("Ambang verifikasi wajah disimpan"); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="flex-1"><Field label="Ambang (default 0,5)"><Input data-testid="settings-face-threshold" inputMode="decimal" className="h-10 font-mono" value={val} onChange={(e) => setVal(e.target.value)} /></Field></div>
      <Button data-testid="settings-face-threshold-save" type="submit" disabled={!s} className="h-10 rounded-full px-6">Simpan ambang</Button>
    </form>
  );
};

const PasswordForm = () => {
  const [f, setF] = useState({ lama: "", baru: "", ulang: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (f.baru.length < 6) return toast.error("Password baru minimal 6 karakter");
    if (f.baru !== f.ulang) return toast.error("Konfirmasi password tidak sama");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { password_lama: f.lama, password_baru: f.baru });
      toast.success("Password berhasil diubah");
      setF({ lama: "", baru: "", ulang: "" });
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={save} className="grid gap-4 sm:grid-cols-3">
      <Field label="Password lama"><Input data-testid="settings-old-password" type="password" className="h-10" value={f.lama} onChange={set("lama")} autoComplete="current-password" /></Field>
      <Field label="Password baru"><Input data-testid="settings-new-password" type="password" className="h-10" value={f.baru} onChange={set("baru")} autoComplete="new-password" /></Field>
      <Field label="Ulangi password baru"><Input data-testid="settings-confirm-password" type="password" className="h-10" value={f.ulang} onChange={set("ulang")} autoComplete="new-password" /></Field>
      <div className="sm:col-span-3"><Button data-testid="settings-password-save" type="submit" disabled={saving} className="h-10 w-full rounded-full px-6 sm:w-auto">{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Ganti password</Button></div>
    </form>
  );
};

export default function Pengaturan() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="space-y-5">
      <PageHeader title="Pengaturan" testid="settings-title" subtitle={`Masuk sebagai ${user?.email}`} />
      <Card icon={Coins} title="Tarif per voucher" desc="Perubahan tarif hanya berlaku untuk periode yang belum dibayar. Periode yang sudah dibayar tetap memakai tarif lama.">
        <TarifForm />
      </Card>
      <Card icon={KeyRound} title="Ganti password"><PasswordForm /></Card>
      <Card icon={ScanFace} title="Verifikasi wajah" desc="Ambang jarak kemiripan wajah. Cocok jika jarak ≤ ambang. Makin kecil makin ketat (disarankan 0,45–0,55).">
        <ThresholdForm />
      </Card>
      <Button data-testid="settings-logout-btn" variant="outline" className="h-10 w-full rounded-full text-red-700 hover:bg-red-50 sm:w-auto" onClick={() => { logout(); navigate("/login"); }}>
        <LogOut className="mr-1.5 h-4 w-4" />Keluar
      </Button>
    </div>
  );
}
